import React, { useState } from "react";
import { X, Loader2, Send, Paperclip, Trash2 } from "lucide-react";
import { toast } from "sonner";

// Story #5 — customer-facing "Request Proposal Services" modal fired from
// the pursuit detail. Preserves attachments (up to 5, ~5 MB each) and
// customer notes.
const SERVICES = [
  { id: "proposal_writing",    label: "Proposal Writing" },
  { id: "capture_management",  label: "Capture Management" },
  { id: "compliance_review",   label: "Compliance Review" },
  { id: "price_to_win",        label: "Price-to-Win Analysis" },
  { id: "teaming",             label: "Teaming / Partner Sourcing" },
  { id: "other",               label: "Other" },
];

const PRIORITIES = ["LOW", "MEDIUM", "HIGH", "URGENT"];

// Read a File → base64 (returns { filename, mime_type, content_base64 })
function fileToBase64(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const s = String(reader.result || "");
      const b64 = s.includes(",") ? s.split(",")[1] : s;
      resolve({ filename: file.name, mime_type: file.type || "application/octet-stream", content_base64: b64 });
    };
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
}

export default function RequestProposalServicesModal({ open, onClose, pursuitId, custApi, opportunityName }) {
  const [service, setService] = useState("proposal_writing");
  const [priority, setPriority] = useState("MEDIUM");
  const [due, setDue] = useState("");
  const [message, setMessage] = useState("");
  const [files, setFiles] = useState([]);
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);

  if (!open) return null;

  const addFiles = async (fileList) => {
    const arr = Array.from(fileList).slice(0, 5 - files.length);
    if (!arr.length) return;
    try {
      const encoded = await Promise.all(arr.map(fileToBase64));
      // Enforce ~5 MB per file (base64 grows ~33%)
      const oversized = encoded.find((f) => f.content_base64.length > 7_000_000);
      if (oversized) { toast.error(`Attachment "${oversized.filename}" is too large (max ~5MB).`); return; }
      setFiles((prev) => [...prev, ...encoded]);
    } catch (e) { toast.error("Could not read attachment."); }
  };
  const removeFile = (i) => setFiles((f) => f.filter((_, idx) => idx !== i));

  const submit = async () => {
    setBusy(true);
    try {
      await custApi.post("/customer/service_requests", {
        pursuit_id: pursuitId,
        requested_service: service,
        priority,
        desired_completion_date: due || null,
        customer_message: message,
        attachments: files,
      });
      setSent(true);
      toast.success("Request submitted. You'll receive an acknowledgment shortly.");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to submit request");
    } finally {
      setBusy(false);
    }
  };

  const close = () => {
    if (busy) return;
    setSent(false); setMessage(""); setFiles([]); setDue(""); setPriority("MEDIUM"); setService("proposal_writing");
    onClose();
  };

  return (
    <div className="fixed inset-0 z-50 bg-slate-950/80 backdrop-blur-sm flex items-center justify-center p-4" onClick={close} data-testid="cust-request-services-modal">
      <div className="bg-[var(--bg-secondary)] border border-[var(--border-soft)] w-full max-w-2xl max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <div className="border-b border-[var(--border-soft)] p-4 flex items-center justify-between">
          <div>
            <div className="tick-label text-sky-400 mb-1">/ Proposal Services Request</div>
            <div className="font-display font-bold text-slate-100">{opportunityName}</div>
          </div>
          <button onClick={close} data-testid="cust-request-services-close" className="text-slate-400 hover:text-slate-100"><X className="w-5 h-5" /></button>
        </div>

        {sent ? (
          <div className="p-8 text-center" data-testid="cust-request-services-success">
            <div className="w-12 h-12 rounded-full bg-emerald-500/20 border border-emerald-500/50 mx-auto flex items-center justify-center mb-4">
              <Send className="w-5 h-5 text-emerald-300" />
            </div>
            <div className="font-display font-bold text-slate-100 text-lg mb-2">Request received</div>
            <p className="text-slate-400 text-sm mb-5 max-w-md mx-auto">A founder will review and reply within one business day. You&apos;ll receive an acknowledgment email shortly.</p>
            <button onClick={close} className="bg-sky-500 hover:bg-sky-400 text-slate-950 font-mono text-[11px] tracking-[0.2em] uppercase font-bold px-4 py-2.5">Done</button>
          </div>
        ) : (
          <div className="p-5 space-y-4">
            <label className="block">
              <span className="tick-label mb-1.5 inline-block">Requested Service</span>
              <select value={service} onChange={(e) => setService(e.target.value)} data-testid="cust-request-services-service"
                className="w-full bg-[var(--bg-primary)] border border-[var(--border-soft)] text-slate-100 text-sm px-3 py-2">
                {SERVICES.map((o) => <option key={o.id} value={o.id}>{o.label}</option>)}
              </select>
            </label>

            <div className="grid sm:grid-cols-2 gap-3">
              <label className="block">
                <span className="tick-label mb-1.5 inline-block">Priority</span>
                <select value={priority} onChange={(e) => setPriority(e.target.value)} data-testid="cust-request-services-priority"
                  className="w-full bg-[var(--bg-primary)] border border-[var(--border-soft)] text-slate-100 text-sm px-3 py-2">
                  {PRIORITIES.map((p) => <option key={p} value={p}>{p}</option>)}
                </select>
              </label>
              <label className="block">
                <span className="tick-label mb-1.5 inline-block">Desired Completion Date</span>
                <input type="date" value={due} onChange={(e) => setDue(e.target.value)} data-testid="cust-request-services-due"
                  className="w-full bg-[var(--bg-primary)] border border-[var(--border-soft)] text-slate-100 text-sm px-3 py-2" />
              </label>
            </div>

            <label className="block">
              <span className="tick-label mb-1.5 inline-block">Customer Message</span>
              <textarea value={message} onChange={(e) => setMessage(e.target.value)} rows={5}
                placeholder="Tell us what you need help with — deadlines, scope, teaming, sections, etc."
                data-testid="cust-request-services-message"
                className="w-full bg-[var(--bg-primary)] border border-[var(--border-soft)] focus:border-sky-500/70 outline-none text-slate-100 text-sm px-3 py-2" />
            </label>

            <div>
              <span className="tick-label mb-1.5 inline-block">Attachments (max 5, ~5 MB each)</span>
              <label className="flex items-center gap-2 border border-dashed border-sky-500/40 bg-sky-500/5 px-4 py-3 text-sm text-slate-300 cursor-pointer hover:border-sky-500/70">
                <Paperclip className="w-4 h-4 text-sky-400" />
                <span>Click to attach files</span>
                <input type="file" multiple onChange={(e) => addFiles(e.target.files)} className="hidden" data-testid="cust-request-services-files" />
              </label>
              {files.length > 0 && (
                <ul className="mt-2 space-y-1">
                  {files.map((f, i) => (
                    <li key={i} className="flex items-center justify-between gap-2 text-xs text-slate-300 border border-[var(--border-soft)] bg-[var(--bg-primary)] px-3 py-1.5">
                      <span className="truncate font-mono">{f.filename}</span>
                      <button onClick={() => removeFile(i)} data-testid={`cust-request-services-file-remove-${i}`} className="text-slate-500 hover:text-rose-300">
                        <Trash2 className="w-3.5 h-3.5" />
                      </button>
                    </li>
                  ))}
                </ul>
              )}
            </div>

            <div className="flex items-center justify-end gap-2 pt-2">
              <button onClick={close} disabled={busy} data-testid="cust-request-services-cancel"
                className="border border-[var(--border-soft)] hover:border-sky-500/60 text-slate-300 font-mono text-[11px] tracking-[0.2em] uppercase px-4 py-2.5">
                Cancel
              </button>
              <button onClick={submit} disabled={busy || !message.trim()} data-testid="cust-request-services-submit"
                className="bg-sky-500 hover:bg-sky-400 disabled:opacity-40 text-slate-950 font-mono text-[11px] tracking-[0.2em] uppercase font-bold px-4 py-2.5 inline-flex items-center gap-2">
                {busy ? <><Loader2 className="w-3.5 h-3.5 animate-spin" /> Submitting…</> : <><Send className="w-3.5 h-3.5" /> Submit Request</>}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
